import React, { useEffect } from 'react';
import Immutable from 'immutable';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  setOperationStatus,
  UI_OPERATION_STATUS_ERROR,
  UI_OPERATION_STATUS_REQUEST,
  UI_OPERATION_STATUS_REASON_UNKNOWN,
} from '@uncinc/react-drupal-core';
import { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import { BlockParingCode, ClientsOverview } from '../../components';
import Page from '../../containers/Page';
import useClasses from '../../hooks/useClasses';
import useOperationStatus from '../../hooks/useOperationStatus';
import useToast from '../../hooks/useToast';

import './style.scss';

interface ClientOverviewItem {
  name: string
  domain: string
  days_registered: number
  weeks_registered: number
  uid: string
}

type ImmutableClientOverviewItem = Immutable.RecordOf<ClientOverviewItem>;

const PageClients: React.FC = () => {
  const { buildClass } = useClasses('PageClients');
  const { t } = useTranslation();
  const toast = useToast();
  const dispatch = useDispatch();

  const fetchClientsOperationStatus = useOperationStatus('nursePractitioner', 'fetchClients');

  const clientsList = useSelector<ImmutableRootState<
  Immutable.List<ImmutableClientOverviewItem>>, Immutable.List<ImmutableClientOverviewItem>>(
    (state) => state.getIn(['nursePractitioner', 'clients'], Immutable.List<ImmutableClientOverviewItem>()),
  );

  const userId = useSelector<ImmutableRootState<number>, number>(
    (state) => state.getIn(['login', 'uid']),
  );

  const pairingCode = useSelector<ImmutableRootState<string>, string>(
    (state) => state.getIn(['user', userId, 'field_poh_pairing_code', 0, 'value']),
  );

  useEffect(() => {
    if (fetchClientsOperationStatus === UI_OPERATION_STATUS_ERROR) {
      toast.show(t('pages.clients.error'), 'error');
    }

    if (fetchClientsOperationStatus !== UI_OPERATION_STATUS_REQUEST) {
      // Consume the operation status so the toast won't show again.
      dispatch(setOperationStatus('nursePractitioner', 'fetchClients', null, UI_OPERATION_STATUS_REASON_UNKNOWN));
    }
  }, [fetchClientsOperationStatus, toast, t, dispatch]);

  return (
    <Page className={buildClass()} title={t('pages.clients.title')}>
      <div className="col-lg-4 py-8 py-lg-12 order-lg-3 order-md-2 order-sm-2 order-2">
        <BlockParingCode pairingCode={pairingCode} />
      </div>
      <div className="col-lg-6 py-8 py-lg-12 order-lg-2 order-md-3 order-sm-3 order-3">
        <h1 className={buildClass('title')}>
          {t('pages.clients.title')}
        </h1>
        <ClientsOverview
          clients={clientsList.toJS()}
          isError={fetchClientsOperationStatus === UI_OPERATION_STATUS_ERROR}
          isLoading={fetchClientsOperationStatus === UI_OPERATION_STATUS_REQUEST}
        />
      </div>
    </Page>
  );
};

export default PageClients;
